import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import ScheduleReservation from 'services/ScheduleReservation'
import CheckBox from 'components/CheckBox'
import Select from 'components/Inputs/Select'
import DateInput from 'components/Inputs/DateInput'
import AlertDialog from 'components/AlertDialog'
import OptionDialog from 'components/OptionDialog'
import editReservationValidator from 'validators/editReservationValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import { Formik } from 'formik'
import { DateTime } from 'luxon'

import {
  InfoText,
  Label,
  Form,
  FormField,
  Input,
  Row,
  WeekdayContainer,
  WarningText,
  WhiteText,
  ErrorContainer
} from './styles'

export default function EditReservationModal ({
  visible,
  date,
  timeInterval,
  data: reservation,
  onConfirm: handleConfirm,
  onDelete: handleDelete,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [deleting, setDeleting] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [weekdays, setWeekdays] = useState ([])
  const [businessHours, setBusinessHours] = useState (null)
  const [professionals, setProfessionals] = useState ([])
  const [startTimeWindows, setStartTimeWindows] = useState ([])
  const [endTimeWindows, setEndTimeWindows] = useState ([])
  const [showEditReservationDialog, setShowEditReservationDialog] = useState (false)
  const [showDeleteReservationDialog, setShowDeleteReservationDialog] = useState (false)
  const selectedISODate = date.toISODate ()
  const previousDayISODate = date.minus ({days: 1}).toISODate ()
  const nextDayISODate = date.plus ({days: 1}).toISODate ()
  const hasEditPermission = AuthService.hasPermission ('agenda', 'edit')
  const hasDeletePermission = AuthService.hasPermission ('agenda', 'delete')
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const { RECURRENCE_OPTIONS } = ScheduleReservation

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const reservationValues = {
    professionalId: reservation.professional_id,
    description: reservation.description,
    startDate: reservation.start_date,
    endDate: reservation.end_date,
    startTime: reservation.start_time,
    endTime: reservation.end_time,
    recurrenceType: reservation.recurrence_type,
    recurrenceInterval: reservation.recurrence_interval,
    weekdays: reservation.weekdays
  }

  useEffect (() => {
    if (visible) getData ()
  }, [visible])

  const getData = async () => {
    try {
      const [{ data: weekdays }, { data: workingDays }, { data: professionals }] = await Promise.all ([
        cutileiApi.get ('/weekdays'),
        cutileiApi.get (`/businesses/${businessId}/working_days`),
        cutileiApi.get (
          `/businesses/${businessId}/professionals?access_level=service&status=active&deleted=false`,
          requestConfig
        )
      ])

      const businessHours = TimeWindow.getBusinessHours(workingDays)

      const dayTimeWindows = businessHours?.opening && businessHours?.closing
        ? TimeWindow.getTimeWindows (businessHours?.opening, businessHours?.closing, false, timeInterval)
        : []

      setStartTimeWindows (dayTimeWindows)
      setEndTimeWindows (dayTimeWindows)

      setWeekdays (weekdays)
      setBusinessHours (businessHours)

      const professionalOptions = professionals.map (professional => ({
        label: professional?.nickname ? professional?.nickname : professional?.name,
        value: professional
      }))

      // A grade busca profissionais com `date=`, que por desenho inclui quem foi excluído
      // depois daquele dia; aqui o filtro é `deleted=false`. Quando o dono da reserva cai
      // fora desta lista ele entra assim mesmo, senão o Select abre sem valor.
      const hasReservationProfessional = professionalOptions.some (option => (
        option.value?.id === reservation.professional_id
      ))

      setProfessionals (hasReservationProfessional || !reservation.professional
        ? professionalOptions
        : [...professionalOptions, {
          label: reservation.professional.nickname ?? reservation.professional.name,
          value: reservation.professional
        }]
      )
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const getPeriod = reservation => {
    const period = DateTime.fromISO (reservation.end_date)
      .diff (DateTime.fromISO (reservation.start_date), ['days', 'months']).toObject ()

    const getLabel = () => {
      if (!period.months) return period.days === 0 || period.days === 7 || period.days === 15
        ? period.days > 0 ? `${period.days} dias` : '1 dia'
        : 'Personalizado'
      else if (period.months >= 5000) return 'Indeterminado'
      else return `${period.months} mês`
    }

    const getValue = () => {
      if (period.months === 1) return 30
      else if (period.months >= 5000) return -1
      else return period.days === 0 || period.days === 7 || period.days === 15 ? period.days : -2
    }

    return { label: getLabel (), value: getValue () }
  }

  const deleteReservation = async () => {
    try {
      setDeleting (true)
      await cutileiApi.delete (`/schedule_reservations/${reservation.id}`, requestConfig)
      handleDelete (reservation)
      handleClose ()
    } catch (error) {
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    } finally {
      setDeleting (false)
    }
  }

  const editDay = async (values, setSubmitting) => {
    const { professional, description, startTime, endTime, recurrence, weekdays } = values

    try {
      await cutileiApi.post (`/businesses/${businessId}/schedule_reservations`, {
        ...reservationValues,
        ...(selectedISODate === reservation.start_date ? {
          startDate: nextDayISODate
        } : {
          endDate: previousDayISODate
        })
      }, requestConfig)

      if (![reservation.start_date, reservation.end_date].includes (selectedISODate))
        cutileiApi.post (`/businesses/${businessId}/schedule_reservations`, {
          ...reservationValues,
          startDate: nextDayISODate
        }, requestConfig)

      const { data: reservationData } = await cutileiApi.put (
        `/schedule_reservations/${reservation.id}`, {
          professionalId: professional.value.id,
          description,
          startDate: selectedISODate,
          endDate: selectedISODate,
          startTime: startTime.value,
          endTime: endTime.value,
          recurrenceType: recurrence.value.recurrenceType,
          recurrenceInterval: recurrence.value.recurrenceInterval,
          weekdays: weekdays.filter (day => day.selected)
        }, requestConfig
      )

      setSubmitting (false)
      handleConfirm (reservationData)
      handleClose ()
    } catch (error) {
      setSubmitting (false)
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    }
  }

  const deleteDay = async () => {
    try {
      setDeleting (true)

      await cutileiApi.put (`/schedule_reservations/${reservation.id}`, {
        ...reservationValues,
        ...(selectedISODate === reservation.start_date ? {
          startDate: nextDayISODate
        } : {
          endDate: previousDayISODate
        })
      }, requestConfig)

      if (![reservation.start_date, reservation.end_date].includes (selectedISODate))
        cutileiApi.post (`/businesses/${businessId}/schedule_reservations`, {
          ...reservationValues,
          startDate: nextDayISODate
        }, requestConfig)

      handleDelete (reservation)
      handleClose ()
    } catch (error) {
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    } finally {
      setDeleting (false)
    }
  }

  const toggleEditReservationDialog = () => setShowEditReservationDialog (!showEditReservationDialog)
  const toggleDeleteReservationDialog = () => setShowDeleteReservationDialog (!showDeleteReservationDialog)
  
  const period = useMemo (() => getPeriod (reservation), [reservation])

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Alterar reserva de agenda</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={editReservationValidator}
            initialValues={{
              'professional': {
                label: (
                  professionals.find (professional => (
                    professional.value.id === reservation.professional_id
                  ))?.label
                  ?? reservation.professional?.nickname
                  ?? reservation.professional?.name
                  ?? 'Profissional'
                ),
                value: reservation.professional
              },
              'description': reservation.description,
              'startDate': reservation.start_date,
              'endDate': reservation.end_date,
              'period': period,
              'startTime': {
                label: reservation.start_time,
                value: reservation.start_time
              },
              'endTime': {
                label: reservation.end_time,
                value: reservation.end_time
              },
              'recurrence': RECURRENCE_OPTIONS.find (recurrence => (
                recurrence.value.recurrenceType === reservation.recurrence_type
              )) ?? RECURRENCE_OPTIONS[0],
              'weekdays': weekdays.map (day => {
                const reservationWeekDay = (reservation.weekdays ?? []).find (weekDay => weekDay.id === day.id)
              
                return !!reservationWeekDay
                  ? { ...reservationWeekDay, selected: true }
                  : { ...day, selected: false }
              })
            }}
            onSubmit={async (values, { setSubmitting }) => {
              const {
                professional, description, startDate, endDate, period,
                startTime, endTime, recurrence, weekdays
              } = values

              try {
                const { data: reservationData } = await cutileiApi.put (`/schedule_reservations/${reservation.id}`, {
                  professionalId: professional.value.id,
                  description,
                  startDate,
                  endDate: period.value === 0 ? startDate : endDate,
                  startTime: startTime.value,
                  endTime: endTime.value,
                  recurrenceType: recurrence.value.recurrenceType,
                  recurrenceInterval: recurrence.value.recurrenceInterval,
                  weekdays: weekdays.filter (day => day.selected)
                }, requestConfig)

                setSubmitting (false)
                handleConfirm (reservationData)
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                setErrorMessage (getApiErrorMessage (error))
                logApiError (error)
              }
            }}
          >
            {function EditReservationForm ({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit,
              setSubmitting
            }) {
              const handleSave = period.value !== 0 ? toggleEditReservationDialog : handleSubmit

              const handleStartDateSelected = startDate => {
                setFieldValue ('startDate', startDate)

                if (values.period) handlePeriodChange ('period', values.period, startDate)
                if (values.period?.value === 0) {
                  setFieldValue ('weekdays', weekdays.map (day => ({
                    ...day, selected: day.iso_number === DateTime.fromISO (startDate).weekday
                  })))
                  setFieldValue ('endDate', startDate)
                }
              }

              const handleEndDateSelected = endDate => {
                const { startDate } = values
                const dateInterval = { startDate, endDate }

                handleSelectAllDays (getPeriodInDays (dateInterval) <= 15, weekdays)
                setFieldValue ('endDate', endDate)
              }

              const handlePeriodChange = (name, option, fromDate = values.startDate) => {
                let interval = null
                const { weekdays } = values
                const startDate = DateTime.fromISO (fromDate)

                switch (option.value) {
                  case -2: interval = null; break // Personalizado
                  case -1: interval = {years: 500}; break // Indeterminado
                  case 30: interval = {months: 1}; break // 1 mês
                  default: interval = {days: option.value} // 1 dia - 7 dias - 15 dias
                }
              
                const endDate = startDate.plus (interval ?? {days: 1}).toISODate ()
                setFieldValue ('endDate', endDate)

                if (option.value !== -1) handleSelectAllDays (option.value <= 15, weekdays)
                if (option.value === 0) setFieldValue ('weekdays', weekdays.map (day => ({
                  ...day, selected: day.iso_number === startDate.weekday
                })))

                setFieldValue (name, option)
              }

              const handleTimeSelected = (name, option) => {
                if (name === 'startTime') {
                  setEndTimeWindows (TimeWindow.getTimeWindows (option.value, businessHours?.closing ?? option.value, true))
                } else if (name === 'endTime') {
                  setStartTimeWindows (TimeWindow.getTimeWindows (businessHours?.opening ?? option.value, option.value, true))
                }
                setFieldValue (name, option)
              }

              const handleSelectAllDays = (value, weekdays) => {
                setFieldValue ('weekdays', weekdays.map (day => ({ ...day, selected: value })))
              }

              const getPeriodInDays = values => {
                const startDate = DateTime.fromISO (values.startDate)
                const endDate = DateTime.fromISO (values.endDate)
                const periodInDays = endDate.diff (startDate, 'days').toObject ().days

                return periodInDays
              }

              const selectAllDays = useMemo (() => (
                values.weekdays.filter (day => day.selected).length === 7
              ), [values.weekdays])

              const periodInDays = useMemo (() => getPeriodInDays (values), [values])
              const showRecurrence = useMemo (() => periodInDays >= 30, [periodInDays])

              useEffect (() => {
                if (!showRecurrence) setFieldValue ('recurrence', RECURRENCE_OPTIONS[0])
              }, [showRecurrence, setFieldValue])

              return (
                <>
                  <DialogBody>
                    <Form onKeyDown={submitOnEnter(handleSave, isSubmitting || !hasEditPermission)}>
                      <Row>
                        <FormField>
                          <Select
                            name='professional'
                            placeholder='Profissionais...'
                            value={values.professional}
                            options={professionals}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            error={errors.professional}
                            touched={touched.professional}
                            isDisabled={!hasEditPermission}
                          />
                          {touched.professional && errors.professional && (
                            <WarningText>
                              {errors.professional}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Input 
                            placeholder='Descrição da reserva'
                            value={values.description}
                            disabled={!hasEditPermission}
                            onChange={handleChange ('description')} 
                            onBlur={handleBlur ('description')}
                          />
                          {touched.description && errors.description && (
                            <WarningText>
                              {errors.description}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      <Row>
                        <FormField>
                          <DateInput
                            placeholder='Data de início'
                            value={values.startDate}
                            disabled={!hasEditPermission}
                            onChange={handleStartDateSelected}
                            style={{width: '100%'}}
                          />
                          {touched.startDate && errors.startDate && (
                            <WarningText>
                              {errors.startDate}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField style={{marginLeft: 5, marginRight: values.period?.value === -2 ? 5 : 0}}>
                          <Select
                            name='period'
                            placeholder='Período...'
                            value={values.period}
                            options={ScheduleReservation.PERIODS}
                            onChange={handlePeriodChange}
                            onBlur={setFieldTouched}
                            error={errors.period}
                            touched={touched.period}
                            isDisabled={!hasEditPermission}
                          />
                          {touched.period && errors.period && (
                            <WarningText>
                              {errors.period}
                            </WarningText>
                          )}
                        </FormField>
                        {values.period?.value === -2 && (
                          <FormField>
                            <DateInput
                              placeholder='Data final'
                              value={values.endDate}
                              disabled={!hasEditPermission}
                              onChange={handleEndDateSelected}
                              style={{width: '100%'}}
                            />
                            {touched.endDate && errors.endDate && (
                              <WarningText>
                                {errors.endDate}
                              </WarningText>
                            )}
                          </FormField>
                        )}
                      </Row>
                      <Row>
                        <FormField>
                          <Select
                            name='startTime'
                            placeholder='Horário inicial...'
                            value={values.startTime}
                            options={startTimeWindows}
                            onChange={handleTimeSelected}
                            onBlur={setFieldTouched}
                            error={errors.startTime}
                            touched={touched.startTime}
                            isDisabled={!hasEditPermission}
                          />
                          {touched.startTime && errors.startTime && (
                            <WarningText>
                              {errors.startTime}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Select
                            name='endTime'
                            placeholder='Horário final...'
                            value={values.endTime}
                            options={endTimeWindows}
                            onChange={handleTimeSelected}
                            onBlur={setFieldTouched}
                            error={errors.endTime}
                            touched={touched.endTime}
                            isDisabled={!hasEditPermission}
                          />
                          {touched.endTime && errors.endTime && (
                            <WarningText>
                              {errors.endTime}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      {periodInDays > 15 && (
                        <FormField style={{marginBottom: 10}}>
                          <Label style={{marginBottom: 5}}>
                            Selecione os dias da semana em que deseja repetir esta reserva
                          </Label>
                          <WeekdayContainer>
                            {values.weekdays.map ((day, index) => (
                              <Row key={index} style={{alignItems: 'center', margin: 0}}>
                                <CheckBox
                                  id={`weekdays.${index}.selected`}
                                  style={{marginRight: 6}}
                                  value={day.selected}
                                  onValueChange={value => setFieldValue (`weekdays.${index}.selected`, value)}
                                  disabled={!hasEditPermission}
                                />
                                <Label htmlFor={`weekdays.${index}.selected`}>
                                  {day.name.substring (0, 3)}
                                </Label>
                              </Row>
                            ))}
                          </WeekdayContainer>
                          <Row style={{alignItems: 'center', marginBottom: 4}}>
                            <CheckBox
                              id='select_all'
                              style={{marginRight: 6}}
                              value={selectAllDays}
                              onValueChange={value => handleSelectAllDays (value, values.weekdays)}
                              disabled={!hasEditPermission}
                            />
                            <Label htmlFor='select_all'>
                              Selecionar todos os dias
                            </Label>
                          </Row>
                          {touched.weekdays && errors.weekdays && (
                            <WarningText>
                              {errors.weekdays}
                            </WarningText>
                          )}
                        </FormField>
                      )}
                      {showRecurrence && (
                        <Row>
                          <FormField>
                            <Label htmlFor='recurrence' style={{marginBottom: 5}}>
                              Recorrência
                            </Label>
                            <Select
                              id='recurrence'
                              name='recurrence'
                              placeholder='Recorrência...'
                              value={values.recurrence}
                              options={RECURRENCE_OPTIONS}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              containerStyles={{width: 250}}
                            />
                          </FormField>
                        </Row>
                      )}
                      {errorMessage && (
                        <ErrorContainer>
                          <WhiteText>{errorMessage}</WhiteText>
                        </ErrorContainer>
                      )}
                      <InfoText style={{marginTop: 7}}>
                        <b>Data de criação:</b>
                        {`  `}
                        {DateTime.fromISO (reservation.created_at).toFormat ('dd/MM/yyyy - H:mm')}
                      </InfoText>
                      {period.value !== 0 ? (
                        <>
                          <OptionDialog
                            visible={showEditReservationDialog}
                            title='Atenção!'
                            message={
                              <InfoText style={{fontSize: 14}}>
                                Deseja alterar a reserva em todos os dias de seu período 
                                ou apenas o dia selecionado ({date.toFormat ('dd/MM/yyyy')})?
                              </InfoText>
                            }
                            options={[
                              {label: 'Alterar reserva', onClick: handleSubmit, dangerous: false},
                              {label: 'Alterar dia', onClick: () => editDay (values, setSubmitting), dangerous: false}
                            ]}
                            onClose={toggleEditReservationDialog}
                            containerStyle={{width: 420}}
                            buttonStyle={{width: 160}}
                          />
                          <OptionDialog
                            visible={showDeleteReservationDialog}
                            title='Atenção!'
                            message={
                              <InfoText style={{fontSize: 14}}>
                                Deseja excluir esta reserva em todos os dias de seu período 
                                ou apenas o dia selecionado ({date.toFormat ('dd/MM/yyyy')})?
                              </InfoText>
                            }
                            options={[
                              {label: 'Excluir reserva', onClick: deleteReservation, dangerous: true},
                              {label: 'Excluir dia', onClick: deleteDay, dangerous: false}
                            ]}
                            onClose={toggleDeleteReservationDialog}
                            containerStyle={{width: 420}}
                            buttonStyle={{width: 160}}
                          />
                        </>
                      ) : (
                        <AlertDialog
                          visible={showDeleteReservationDialog}
                          title='Atenção!'
                          message='Deseja realmente excluir esta reserva de agenda?'
                          confirmText='Sim'
                          cancelText='Não'
                          dangerous={true}
                          onConfirm={deleteReservation}
                          onClose={toggleDeleteReservationDialog}
                        />
                      )}
                    </Form>
                  </DialogBody>
                  {(hasEditPermission || hasDeletePermission) && (
                    <DialogFooter>
                      {hasDeletePermission && (
                        <MainButton
                          label='Excluir reserva'
                          onClick={toggleDeleteReservationDialog}
                          dangerous={true}
                          disabled={deleting}
                          loading={deleting}
                          width={180}
                        />
                      )}
                      {hasEditPermission && (
                        <MainButton
                          label='Salvar alterações'
                          onClick={handleSave}
                          disabled={isSubmitting}
                          loading={isSubmitting}
                          width={180}
                        />
                      )}
                    </DialogFooter>
                  )}
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
